'use client';

import { useState, useRef, useEffect } from 'react';
import { getText } from '@/utils/text/text';

import './styles/@theme';
import { useGlobalContext } from '@/store/contexts/global-context';
import { classes } from '@/utils/classes/classes';

const SLIDE_UP = 'slide-up';
const SLIDE_DOWN = 'slide-down';
const DELTA_Y = 50;

export type GameInfoModalWrapperVariant = 'biggest-win' | 'video-web';

interface GameInfoModalWrapperProps {
    show: boolean;
    onClose: () => void;
    backgroundImgUrl?: string;
    children: React.ReactNode;
    isNarrow?: boolean;
    closeIcon?: React.ReactNode | null;
    headerTitle?: React.ReactNode;
    variant?: GameInfoModalWrapperVariant;
    isMobileViewport?: boolean;
}

export const GameInfoModalWrapper = ({
    show,
    onClose,
    backgroundImgUrl,
    children,
    isNarrow = false,
    closeIcon = null,
    headerTitle = null,
    variant,
    isMobileViewport
}: GameInfoModalWrapperProps) => {
    const [animationClass, setAnimationClass] = useState(SLIDE_UP);
    const [isVisible, setIsVisible] = useState(show);
    const touchStartY = useRef<number | null>(null);
    const touchCurrentY = useRef<number | null>(null);
    const { isMobile, isDesktop } = useGlobalContext();
    const effectiveIsMobile = isMobileViewport ?? isMobile;
    const effectiveIsDesktop = isMobileViewport ? false : isDesktop;

    const handleShow = () => {
        setAnimationClass(SLIDE_UP);
        setIsVisible(true);
    };

    const handleHide = () => {
        setAnimationClass(SLIDE_DOWN);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (effectiveIsMobile) {
            touchStartY.current = e?.touches?.[0]?.clientY;
            touchCurrentY.current = touchStartY.current;
        }
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (touchStartY.current === null) {
            return;
        }

        touchCurrentY.current = e.touches[0]?.clientY ?? null;
    };

    const handleTouchEnd = () => {
        if (
            touchStartY.current !== null &&
            touchCurrentY.current !== null &&
            touchCurrentY.current - touchStartY.current >= DELTA_Y
        ) {
            setAnimationClass(SLIDE_DOWN);
        }

        touchStartY.current = null;
        touchCurrentY.current = null;
    };

    const handleAnimationEnd = () => {
        if (animationClass === SLIDE_DOWN) {
            setIsVisible(false);
            onClose();
        }
    };

    const handleClose = () => {
        setAnimationClass(SLIDE_DOWN);
        if (!effectiveIsMobile) {
            onClose();
        }
    };

    const handleShowEffect = () => {
        if (!isVisible) {
            handleShow();
        }
    };

    const handleHideEffect = () => {
        if (isVisible) {
            handleHide();
        }
    };

    const handleShowModal = (showModal: boolean) => {
        if (showModal) {
            handleShowEffect();
        } else {
            handleHideEffect();
        }
    };

    useEffect(() => {
        handleShowModal(show);
    }, [show, isVisible]);

    if (!isVisible || (!effectiveIsMobile && !show)) {
        return null;
    }

    const wrapperClassName = classes('game-info-modal-wrapper', variant && `game-info-modal-wrapper--${variant}`);

    return (
        <div
            className={wrapperClassName}
            data-testid="game-info-modal-wrapper"
            data-t-gameinfomodal="true"
            data-narrow={isNarrow}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}>
            <div
                // isDesktop is needed, media query does not cover all ipad widths
                className={classes(`modal-background ${animationClass} ${effectiveIsDesktop && 'desktop'}`)}
                style={{ backgroundImage: `url(${backgroundImgUrl})` }}
                onAnimationEnd={handleAnimationEnd}
                data-testid="modal-background">
                <div className={'modal-component'}>
                    <div
                        className={classes(`modal-header${headerTitle !== null ? ' modal-header--with-title' : ''}`)}
                        data-testid="gim-modal-header">
                        {headerTitle !== null ? <span className="modal-header__title">{headerTitle}</span> : null}
                        <button
                            onClick={handleClose}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleClose();
                                }
                            }}
                            className="modal-close-button"
                            aria-label={getText({ text: 'common.close' })}
                            data-testid="gim-modal-close-button">
                            {closeIcon || getText({ text: 'common.close' })}
                        </button>
                    </div>
                    <div className="modal-content">{children}</div>
                </div>
            </div>
            <div onClick={handleClose} onKeyDown={handleClose} className="background-overlay" role="none"></div>
        </div>
    );
};
